/**
 * astro-core.js — sun & moon position, shared.
 *
 * WHY THIS EXISTS
 * ---------------
 * The same SunCalc-derived astronomy was pasted into /sun-moon-tracker and
 * (in a lower-precision variant) into earth-globe-astro.js, and /compass needs
 * it a third time. The compass use is not decoration: the sun's azimuth is the
 * ONE heading reference that needs no magnetometer, so on the ~1/3 of visitors
 * whose device has no usable compass sensor (measured: 195 of 534 permission
 * events on /gyroscope-test report `no_sensor`) it is the only thing standing
 * between the user and a dead page. A shared, unit-tested copy is the
 * difference between "the fallback works" and "the fallback was never run".
 *
 * Algorithms from SunCalc (Vladimir Agafonkin, BSD-2-Clause) — the same code
 * that already shipped in /sun-moon-tracker, moved rather than rewritten, with
 * `tests/astro-core.test.mjs` asserting the move changed no output anywhere.
 *
 * All angles OUT of this module are radians (SunCalc convention) except the
 * *Deg helpers, which return compass degrees (0 = north, clockwise) because
 * that is what a compass rose consumes.
 *
 * API
 * ---
 *   AstroCore.sunPos(date, lat, lon)    -> {azimuth, altitude}   radians
 *   AstroCore.moonPos(date, lat, lon)   -> {azimuth, altitude, dist}
 *   AstroCore.sunTimes(date, lat, lon)  -> {sunrise, sunset, ...} Dates
 *   AstroCore.moonTimes(date, lat, lon) -> {rise, set} | {alwaysUp|alwaysDown}
 *   AstroCore.moonIllum(date)           -> {fraction, phase}
 *   AstroCore.sunAzimuthDeg(date, lat, lon)   -> 0..360 from true north
 *   AstroCore.moonAzimuthDeg(date, lat, lon)  -> 0..360 from true north
 *   AstroCore.sunTimeAzimuths(date, lat, lon) -> {sunrise, sunset} degrees|null
 */
(function (root, factory) {
    var AstroCore = factory();
    if (typeof window !== 'undefined') window.AstroCore = AstroCore;
    if (typeof module !== 'undefined' && module.exports) module.exports = AstroCore;
}(this, function () {
    'use strict';

    var PI = Math.PI, rad = PI / 180, dayMs = 864e5,
        J1970 = 2440588, J2000 = 2451545, e = rad * 23.4397;

    function toJulian(d) { return d.valueOf() / dayMs - 0.5 + J1970; }
    function fromJulian(j) { return isNaN(j) ? null : new Date((j + 0.5 - J1970) * dayMs); }
    function toDays(d) { return toJulian(d) - J2000; }
    function ra(l, b) { return Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l)); }
    function dec(l, b) { return Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l)); }
    function azi(H, phi, d) { return Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(d) * Math.cos(phi)); }
    function alt(H, phi, d) { return Math.asin(Math.sin(phi) * Math.sin(d) + Math.cos(phi) * Math.cos(d) * Math.cos(H)); }
    function sidereal(d, lw) { return rad * (280.16 + 360.9856235 * d) - lw; }
    function refr(h) { if (h < 0) h = 0; return 0.0002967 / Math.tan(h + 0.00312536 / (h + 0.08901179)); }
    function sma(d) { return rad * (357.5291 + 0.98560028 * d); }
    function ecl(M) { return M + rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)) + rad * 102.9372 + PI; }
    function sunCoords(d) { var M = sma(d), L = ecl(M); return { dec: dec(L, 0), ra: ra(L, 0) }; }

    function sunPos(date, lat, lng) {
        var lw = rad * -lng, phi = rad * lat, d = toDays(date), c = sunCoords(d), H = sidereal(d, lw) - c.ra;
        return { azimuth: azi(H, phi, c.dec), altitude: alt(H, phi, c.dec) };
    }

    var TIMES = [
        [-0.833, 'sunrise', 'sunset'], [-6, 'dawn', 'dusk'],
        [-12, 'nauticalDawn', 'nauticalDusk'], [-18, 'nightEnd', 'night'],
        [6, 'goldenHourEnd', 'goldenHour']
    ];
    function jcycle(d, lw) { return Math.round(d - 0.0009 - lw / (2 * PI)); }
    function atransit(Ht, lw, n) { return 0.0009 + (Ht + lw) / (2 * PI) + n; }
    function stransit(ds, M, L) { return J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L); }
    function hourAngle(h, phi, d) { return Math.acos((Math.sin(h) - Math.sin(phi) * Math.sin(d)) / (Math.cos(phi) * Math.cos(d))); }
    function setJ(h, lw, phi, d, n, M, L) { return stransit(atransit(hourAngle(h, phi, d), lw, n), M, L); }

    function sunTimes(date, lat, lng) {
        var lw = rad * -lng, phi = rad * lat, d = toDays(date), n = jcycle(d, lw), ds = atransit(0, lw, n),
            M = sma(ds), L = ecl(M), dc = dec(L, 0), Jnoon = stransit(ds, M, L),
            r = { solarNoon: fromJulian(Jnoon), nadir: fromJulian(Jnoon - 0.5) };
        for (var i = 0; i < TIMES.length; i++) {
            var t = TIMES[i], Jset = setJ(t[0] * rad, lw, phi, dc, n, M, L), Jrise = Jnoon - (Jset - Jnoon);
            r[t[1]] = fromJulian(Jrise); r[t[2]] = fromJulian(Jset);
        }
        return r;
    }

    function moonCoords(d) {
        var L = rad * (218.316 + 13.176396 * d), M = rad * (134.963 + 13.064993 * d), F = rad * (93.272 + 13.229350 * d),
            l = L + rad * 6.289 * Math.sin(M), b = rad * 5.128 * Math.sin(F), dt = 385001 - 20905 * Math.cos(M);
        return { ra: ra(l, b), dec: dec(l, b), dist: dt };
    }
    function moonPos(date, lat, lng) {
        var lw = rad * -lng, phi = rad * lat, d = toDays(date), c = moonCoords(d),
            H = sidereal(d, lw) - c.ra, h = alt(H, phi, c.dec);
        return { azimuth: azi(H, phi, c.dec), altitude: h + refr(h), dist: c.dist };
    }
    function moonIllum(date) {
        var d = toDays(date), s = sunCoords(d), m = moonCoords(d), sd = 149598000,
            phi = Math.acos(Math.sin(s.dec) * Math.sin(m.dec) + Math.cos(s.dec) * Math.cos(m.dec) * Math.cos(s.ra - m.ra)),
            inc = Math.atan2(sd * Math.sin(phi), m.dist - sd * Math.cos(phi)),
            angle = Math.atan2(Math.cos(s.dec) * Math.sin(s.ra - m.ra), Math.sin(s.dec) * Math.cos(m.dec) - Math.cos(s.dec) * Math.sin(m.dec) * Math.cos(s.ra - m.ra));
        return { fraction: (1 + Math.cos(inc)) / 2, phase: 0.5 + 0.5 * inc * (angle < 0 ? -1 : 1) / PI };
    }
    function hoursLater(date, h) { return new Date(date.valueOf() + h * dayMs / 24); }
    function moonTimes(date, lat, lng) {
        var t = new Date(date); t.setHours(0, 0, 0, 0);
        var hc = 0.133 * rad, h0 = moonPos(t, lat, lng).altitude - hc, rise, set, ye;
        for (var i = 1; i <= 24; i += 2) {
            var h1 = moonPos(hoursLater(t, i), lat, lng).altitude - hc, h2 = moonPos(hoursLater(t, i + 1), lat, lng).altitude - hc,
                a = (h0 + h2) / 2 - h1, b = (h2 - h0) / 2, xe = -b / (2 * a); ye = (a * xe + b) * xe + h1;
            var d = b * b - 4 * a * ye, roots = 0, x1, x2, dx;
            if (d >= 0) {
                dx = Math.sqrt(d) / (Math.abs(a) * 2); x1 = xe - dx; x2 = xe + dx;
                if (Math.abs(x1) <= 1) roots++; if (Math.abs(x2) <= 1) roots++; if (x1 < -1) x1 = x2;
            }
            if (roots === 1) { if (h0 < 0) rise = i + x1; else set = i + x1; }
            else if (roots === 2) { rise = i + (ye < 0 ? x2 : x1); set = i + (ye < 0 ? x1 : x2); }
            if (rise && set) break;
            h0 = h2;
        }
        var r = {}; if (rise) r.rise = hoursLater(t, rise); if (set) r.set = hoursLater(t, set);
        if (!rise && !set) r[ye > 0 ? 'alwaysUp' : 'alwaysDown'] = true;
        return r;
    }

    // ── Compass-facing helpers ────────────────────────────────────────────────
    // SunCalc measures azimuth from SOUTH, growing westward; a compass rose
    // measures from NORTH, growing clockwise. Converting at the boundary (once)
    // instead of at each call site is what keeps the sun marker off by 180°
    // from ever being a possibility again.
    function toCompassDeg(azimuthRad) {
        var d = (azimuthRad * 180 / PI + 180) % 360;
        return d < 0 ? d + 360 : d;
    }
    function sunAzimuthDeg(date, lat, lng) { return toCompassDeg(sunPos(date, lat, lng).azimuth); }
    function moonAzimuthDeg(date, lat, lng) { return toCompassDeg(moonPos(date, lat, lng).azimuth); }
    function sunAltitudeDeg(date, lat, lng) { return sunPos(date, lat, lng).altitude * 180 / PI; }
    function moonAltitudeDeg(date, lat, lng) { return moonPos(date, lat, lng).altitude * 180 / PI; }

    /**
     * Azimuths of today's sunrise and sunset — the classic paper-map trick for
     * orienting without any instrument. null on polar days when the event does
     * not happen (SunCalc returns an Invalid Date there; printing "NaN°" beside
     * a horizon marker is worse than printing nothing).
     */
    function sunTimeAzimuths(date, lat, lng) {
        var t = sunTimes(date, lat, lng);
        var out = { sunrise: null, sunset: null };
        if (t.sunrise && !isNaN(t.sunrise)) out.sunrise = sunAzimuthDeg(t.sunrise, lat, lng);
        if (t.sunset && !isNaN(t.sunset)) out.sunset = sunAzimuthDeg(t.sunset, lat, lng);
        return out;
    }

    return {
        sunPos: sunPos, moonPos: moonPos, sunTimes: sunTimes, moonTimes: moonTimes,
        moonIllum: moonIllum, toCompassDeg: toCompassDeg,
        sunAzimuthDeg: sunAzimuthDeg, moonAzimuthDeg: moonAzimuthDeg,
        sunAltitudeDeg: sunAltitudeDeg, moonAltitudeDeg: moonAltitudeDeg,
        sunTimeAzimuths: sunTimeAzimuths
    };
}));
